import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API, LOGO_URL, getAuthHeaders } from "@/lib/api";
import {
  Sparkle, Key, Image, ArrowRight, CheckCircle,
  VideoCamera, Microphone, Lightning, DiscordLogo, GraduationCap, GoogleDriveLogo, Lock,
} from "@phosphor-icons/react";

const INDUSTRIES = [
  "Beauty & Fashion", "Health & Wellness", "Technology & AI",
  "Real Estate", "Finance & Investing", "Food & Beverage",
  "Education & Coaching", "Fitness & Sports", "Travel & Lifestyle",
  "E-commerce & Retail", "Entertainment & Media", "Other",
];

const API_KEY_GUIDES = [
  {
    service: "fal.ai (VEED Fabric 1.0)",
    purpose: "PRIMARY talking-head video model — fast renders (~1-2 min), pay-as-you-go, no monthly minimum (REQUIRED for Auto Create)",
    url: "https://fal.ai/dashboard/keys",
    steps: [
      "Go to fal.ai and sign in with Google",
      "Go to Dashboard → Keys",
      "Click 'New Key' and copy the key (format: <id>:<secret>)",
      "Pricing: $0.10/sec at 480p, $0.20/sec at 720p — pay-as-you-go, no monthly minimum",
      "Top up ~$5-10 to start. Rendering will fail with 'exhausted balance' when your fal.ai account is empty.",
    ],
    required: true,
  },
  {
    service: "muapi.ai",
    purpose: "AI Twin image generation, Scene Studio, outfit swap, B-roll, captions, Veo 3.1 cinematic mode, MiniMax voice cloning (REQUIRED)",
    url: "https://muapi.ai",
    steps: [
      "Sign up at muapi.ai",
      "Navigate to your dashboard → API section",
      "Copy your API key",
      "Top up $10-20 to start (scenes ~$0.05-0.10, B-roll ~$0.15 each, MiniMax clone ~$0.10)",
    ],
    required: true,
  },
  {
    service: "Magic Hour",
    purpose: "Fallback talking-photo renderer — used when fal.ai isn't configured or for >30s clips (OPTIONAL)",
    url: "https://magichour.ai",
    steps: [
      "Sign up at magichour.ai",
      "Click your profile → Developer Hub → API Keys",
      "Click 'Create new key', name it 'The Branding Bar', copy the key (starts with mhk_live_)",
      "Recommended plan: Creator ($12/mo = 10,000 credits, enough for ~3-4 × 60s videos/month)",
      "Only needed if you want a fal.ai backup — the Auto tier automatically falls back.",
    ],
    required: false,
  },
  {
    service: "ElevenLabs",
    purpose: "Voice cloning — clone your voice in ~1 min of audio. Free tier covers 10k chars/mo (OPTIONAL — free OpenAI stock voices are always available)",
    url: "https://elevenlabs.io/app/sign-up",
    steps: [
      "Sign up FREE at elevenlabs.io (no credit card required)",
      "Go to Settings → API Keys → Create New Key",
      "IMPORTANT: Set permissions to 'Has Access' for ALL scopes (Voices, Text to Speech, Voice Cloning). The default 'Restricted' preset gets rejected.",
      "Copy the key (starts with sk_ or xi_)",
      "You can also clone voices in-app via the new Clone My Voice studio (uses this key or muapi)",
    ],
    required: false,
  },
];

const WALKTHROUGH_STEPS = [
  {
    icon: Key,
    title: "Set Up Your 2 Required API Keys",
    desc: "Head to Credits & Keys → API Keys. Paste your fal.ai key (talking-head videos) and muapi.ai key (images + hosting + cinematic). ElevenLabs is optional — free OpenAI voices ship out of the box.",
  },
  {
    icon: Image,
    title: "Upload Your AI Twin",
    desc: "Go to AI Twin Gallery → Create tab. Upload 3-5 high-quality photos of yourself. Different outfits and angles work best. Then try the Scenes tab to place yourself in 62 different settings.",
  },
  {
    icon: Microphone,
    title: "Clone Your Voice (Optional)",
    desc: "Go to Clone My Voice. Record 60s of clean audio and pick ElevenLabs or MiniMax (via muapi). Your cloned voice automatically appears in every voice picker across the app.",
  },
  {
    icon: VideoCamera,
    title: "Create Your First Video",
    desc: "Go to Auto Create, pick a topic and your AI Twin photo, then hit Generate. The system writes a script, generates the voice, and renders a lip-synced video (~5-14 min). Close the tab — a bell notification fires when ready.",
  },
  {
    icon: Lightning,
    title: "Build Your Content Pipeline",
    desc: "Use Content Pipeline to organize ideas, track what's scripted, filmed, and posted. Auto Create saves everything here.",
  },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [brandName, setBrandName] = useState("");
  const [industry, setIndustry] = useState("");
  const [brandColor, setBrandColor] = useState("#E91E8C");
  const [brandDescription, setBrandDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [expandedGuide, setExpandedGuide] = useState(null);

  const totalSteps = 4;

  const handleSaveBrand = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/auth/onboard`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ brand_name: brandName, industry, brand_color: brandColor, brand_description: brandDescription }),
      });
      if (res.ok) {
        await refreshUser();
        setStep(3);
      }
    } catch (err) {
      console.error("Onboarding error:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 py-8" data-testid="onboarding-page">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="The Branding Bar" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-1">
            {step === 1 ? "Set up your brand" : step === 2 ? "Choose your color" : step === 3 ? "Get your API keys" : "You're all set!"}
          </h1>
          <div className="flex items-center justify-center gap-2 mt-3">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className={`h-1.5 rounded-full transition-all ${s <= step ? "bg-[#E91E8C] w-8" : "bg-zinc-700 w-4"}`} />
            ))}
          </div>
          <p className="text-zinc-500 text-sm mt-2">Step {step} of {totalSteps}</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 sm:p-8">

          {/* Step 1: Brand Name + Industry */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Brand Name</label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g. The Branding Bar"
                  data-testid="brand-name-input"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Industry</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {INDUSTRIES.map((ind) => (
                    <button
                      key={ind}
                      onClick={() => setIndustry(ind)}
                      data-testid={`industry-${ind.toLowerCase().replace(/\s|&/g, "-")}`}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${industry === ind ? "bg-[#E91E8C] text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}
                    >
                      {ind}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Tell us about your brand</label>
                <p className="text-zinc-500 text-xs mb-2">Describe your brand, what you offer, your goals, and your target audience. The AI will use this to create content specific to your business.</p>
                <textarea
                  value={brandDescription}
                  onChange={(e) => setBrandDescription(e.target.value)}
                  placeholder="Example: I'm a business coach helping female entrepreneurs scale to 6 figures using social media marketing. My audience is women aged 25-45 who are starting or growing their online businesses. My main offer is a 12-week group coaching program..."
                  rows={5}
                  data-testid="brand-description-input"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#E91E8C] focus:border-transparent text-sm resize-none"
                />
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={!brandName || !industry}
                data-testid="onboarding-next-btn"
                className="w-full py-3 bg-[#E91E8C] text-white font-semibold rounded-xl hover:bg-[#d4177d] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                Next <ArrowRight size={18} />
              </button>
            </div>
          )}

          {/* Step 2: Brand Color */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Brand Color</label>
                <div className="flex items-center gap-4">
                  <input type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)} data-testid="brand-color-input" className="w-16 h-16 rounded-xl cursor-pointer bg-transparent border-2 border-zinc-700" />
                  <div>
                    <p className="text-white font-mono text-lg">{brandColor}</p>
                    <p className="text-zinc-500 text-sm">Your dashboard accent color</p>
                  </div>
                </div>
              </div>
              <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
                <p className="text-zinc-300 text-sm font-medium mb-2">Preview</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full" style={{ backgroundColor: brandColor }} />
                  <div>
                    <p className="text-white font-semibold">{brandName}</p>
                    <p className="text-zinc-500 text-xs">{industry}</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 py-3 bg-zinc-800 text-zinc-300 font-semibold rounded-xl hover:bg-zinc-700 transition-all">Back</button>
                <button onClick={handleSaveBrand} disabled={saving} data-testid="onboarding-save-btn" className="flex-1 py-3 bg-[#E91E8C] text-white font-semibold rounded-xl hover:bg-[#d4177d] disabled:opacity-50 transition-all flex items-center justify-center gap-2">
                  {saving ? "Saving..." : <>Next <ArrowRight size={18} /></>}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: API Key Setup Guide */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                <p className="text-amber-300 text-sm font-semibold mb-1">Important: You need 2 API keys to use Auto Create</p>
                <p className="text-amber-300/80 text-sm">fal.ai (talking-head videos) and muapi.ai (images + hosting + cinematic) are required. Each is BYOK — you pay the providers directly at cost. ElevenLabs and Magic Hour are optional. You can set them up now or later in Credits &amp; Keys.</p>
              </div>
              {API_KEY_GUIDES.map((guide, i) => (
                <div key={guide.service} className="border border-zinc-700 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedGuide(expandedGuide === i ? null : i)}
                    data-testid={`api-guide-${guide.service.toLowerCase().replace(/[\s.]/g, "-")}`}
                    className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-all"
                  >
                    <div className="flex items-center gap-3 text-left">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-semibold text-sm">{guide.service}</p>
                          {guide.required && (
                            <span className="px-1.5 py-0.5 bg-[#E91E8C]/20 text-[#E91E8C] text-[10px] font-bold rounded-md uppercase">Required</span>
                          )}
                        </div>
                        <p className="text-zinc-500 text-xs">{guide.purpose}</p>
                      </div>
                    </div>
                    <ArrowRight size={16} className={`text-zinc-500 transition-transform ${expandedGuide === i ? "rotate-90" : ""}`} />
                  </button>
                  {expandedGuide === i && (
                    <div className="px-4 pb-4 border-t border-zinc-700/50">
                      <ol className="space-y-2 mt-3">
                        {guide.steps.map((s, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-zinc-300">
                            <span className="text-[#E91E8C] font-bold mt-0.5">{j + 1}.</span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ol>
                      <a href={guide.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-[#E91E8C] text-sm font-medium hover:underline">
                        Visit {guide.service} <ArrowRight size={14} />
                      </a>
                    </div>
                  )}
                </div>
              ))}
              <div className="flex gap-3">
                <button onClick={() => setStep(4)} className="flex-1 py-3 bg-zinc-800 text-zinc-300 font-semibold rounded-xl hover:bg-zinc-700 transition-all">
                  Skip for now
                </button>
                <button onClick={() => setStep(4)} data-testid="onboarding-keys-next" className="flex-1 py-3 bg-[#E91E8C] text-white font-semibold rounded-xl hover:bg-[#d4177d] transition-all flex items-center justify-center gap-2">
                  Got it! <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Getting Started Walkthrough */}
          {step === 4 && (
            <div className="space-y-5">
              <p className="text-zinc-400 text-sm text-center mb-2">Here's how to get the most out of your dashboard:</p>

              {WALKTHROUGH_STEPS.map((ws, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#E91E8C]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ws.icon size={20} weight="duotone" className="text-[#E91E8C]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{ws.title}</p>
                    <p className="text-zinc-400 text-sm leading-relaxed">{ws.desc}</p>
                  </div>
                </div>
              ))}

              {/* Community links */}
              <div className="border-t border-zinc-700 pt-5 mt-6">
                <p className="text-zinc-300 text-sm font-semibold mb-3">Join our community</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <a
                    href="https://www.skool.com/the-branding-bar-1662/about?ref=91ffe0d4be8342c79aa821533a969b33"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="community-skool-link"
                    className="flex items-center gap-2 px-4 py-3 bg-zinc-800 rounded-xl border border-zinc-700 hover:border-[#E91E8C]/50 transition-all"
                  >
                    <GraduationCap size={20} weight="duotone" className="text-[#E91E8C]" />
                    <div>
                      <p className="text-white text-sm font-semibold">Skool Community</p>
                      <p className="text-zinc-500 text-xs">Coaching & share creations</p>
                    </div>
                  </a>
                  <a
                    href="https://discord.gg/BmwfRzykqX"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="community-discord-link"
                    className="flex items-center gap-2 px-4 py-3 bg-zinc-800 rounded-xl border border-zinc-700 hover:border-[#5865F2]/50 transition-all"
                  >
                    <DiscordLogo size={20} weight="duotone" className="text-[#5865F2]" />
                    <div>
                      <p className="text-white text-sm font-semibold">Discord Support</p>
                      <p className="text-zinc-500 text-xs">Get help & updates</p>
                    </div>
                  </a>
                  <div
                    data-testid="community-drive-link-locked"
                    className="flex items-center gap-2 px-4 py-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50 opacity-60 cursor-not-allowed relative"
                  >
                    <GoogleDriveLogo size={20} weight="duotone" className="text-zinc-500" />
                    <div>
                      <p className="text-zinc-400 text-sm font-semibold flex items-center gap-1.5">AI Twin Images <Lock size={12} weight="fill" /></p>
                      <p className="text-zinc-600 text-xs">Subscribe to unlock</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleGoToDashboard}
                data-testid="onboarding-finish-btn"
                className="w-full py-3.5 bg-[#E91E8C] text-white font-bold rounded-xl hover:bg-[#d4177d] transition-all flex items-center justify-center gap-2 text-lg mt-4"
              >
                <Sparkle size={20} weight="fill" /> Launch Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
